// Dense body mesh over MediaPipe Pose's 33 landmarks.
//
// MediaPipe only gives us 33 anchor points, but we can build a surprisingly
// dense surface from them using two tricks:
//
//   1. SYNTHETIC VERTICES — we compute extra "virtual" points by interpolating
//      between real landmarks (e.g. midway between shoulders → upper-chest
//      point; midway between hips → belly point). This lets us split the
//      torso into multiple quads so it reads as a curved surface instead of
//      one flat rectangle.
//
//   2. SUBDIVISION — every triangle in the base topology gets recursively
//      subdivided at render time. Each sub-vertex's 2D image-coord and 3D
//      world-coord are both linearly interpolated, so we get a per-sub-
//      triangle normal for smooth shading. A 2-level subdivision turns a
//      single triangle into 16 smaller ones.
//
// Combined: ~25 base triangles × 16 subdivisions ≈ 400 shaded polygons
// running at 60 fps on the browser's canvas 2D.

// ============================================================================
// Landmark indices (real MediaPipe Pose landmarks, 0..32)
// ============================================================================
export const LM = {
  NOSE: 0,
  LEFT_EYE_INNER: 1,
  LEFT_EYE: 2,
  LEFT_EYE_OUTER: 3,
  RIGHT_EYE_INNER: 4,
  RIGHT_EYE: 5,
  RIGHT_EYE_OUTER: 6,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  MOUTH_LEFT: 9,
  MOUTH_RIGHT: 10,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_PINKY: 17,
  RIGHT_PINKY: 18,
  LEFT_INDEX: 19,
  RIGHT_INDEX: 20,
  LEFT_THUMB: 21,
  RIGHT_THUMB: 22,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
  LEFT_HEEL: 29,
  RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31,
  RIGHT_FOOT_INDEX: 32,
} as const;

// ============================================================================
// Synthetic vertex definitions.
//
// A synthetic vertex is a weighted combination of real landmarks. At render
// time the renderer computes its 2D + 3D position by averaging the source
// landmarks. The index range for synthetic vertices starts at 33 (after the
// 33 real landmarks) so triangles can reference them the same way.
// ============================================================================

export interface SyntheticDef {
  /** Index in the expanded vertex array (>= 33) */
  index: number;
  /** Real landmark indices whose positions to average. */
  sources: number[];
  /** Optional weights (same length as sources). Defaults to uniform. */
  weights?: number[];
  /** Optional Z offset in metres, added after averaging. Used to push
   *  midline vertices forward to fake chest / belly curvature. */
  zOffset?: number;
}

const REAL_COUNT = 33;

// Torso midline points — these fake the curvature by pulling the midline
// slightly forward in Z (toward the camera) so when shaded they read as a
// bulge instead of a flat plate.
const CHEST = REAL_COUNT + 0;
const BELLY = REAL_COUNT + 1;
// A midline "spine" point — midway between shoulders and hips, slightly
// further forward again. Lets us split the torso into 4 quads instead of 2.
const STERNUM = REAL_COUNT + 2;
const NAVEL = REAL_COUNT + 3;

// Face midline — between nose and mouth, and a forehead point.
const FOREHEAD = REAL_COUNT + 4;
const CHIN = REAL_COUNT + 5;
const MOUTH_MID = REAL_COUNT + 6;

// Upper arm thickening — midpoints along shoulder→elbow and elbow→wrist,
// with a slight outward push so arms have volume.
const LEFT_UPPER_ARM_MID = REAL_COUNT + 7;
const LEFT_FOREARM_MID = REAL_COUNT + 8;
const RIGHT_UPPER_ARM_MID = REAL_COUNT + 9;
const RIGHT_FOREARM_MID = REAL_COUNT + 10;

// Thigh / shin midpoints — same trick for legs.
const LEFT_THIGH_MID = REAL_COUNT + 11;
const LEFT_SHIN_MID = REAL_COUNT + 12;
const RIGHT_THIGH_MID = REAL_COUNT + 13;
const RIGHT_SHIN_MID = REAL_COUNT + 14;

export const SYNTHETIC_VERTICES: SyntheticDef[] = [
  // Torso column — push midline forward to fake chest curvature
  { index: CHEST, sources: [LM.LEFT_SHOULDER, LM.RIGHT_SHOULDER], zOffset: -0.08 },
  { index: BELLY, sources: [LM.LEFT_HIP, LM.RIGHT_HIP], zOffset: -0.06 },
  {
    index: STERNUM,
    sources: [LM.LEFT_SHOULDER, LM.RIGHT_SHOULDER, LM.LEFT_HIP, LM.RIGHT_HIP],
    weights: [0.38, 0.38, 0.12, 0.12],
    zOffset: -0.09,
  },
  {
    index: NAVEL,
    sources: [LM.LEFT_SHOULDER, LM.RIGHT_SHOULDER, LM.LEFT_HIP, LM.RIGHT_HIP],
    weights: [0.12, 0.12, 0.38, 0.38],
    zOffset: -0.07,
  },

  // Face
  // Forehead: above the eye midpoint. We go up from the nose direction by
  // using ears (higher weight) and nose (lower weight) to extrapolate.
  { index: FOREHEAD, sources: [LM.LEFT_EAR, LM.RIGHT_EAR, LM.NOSE], weights: [0.4, 0.4, 0.2], zOffset: -0.04 },
  { index: CHIN, sources: [LM.MOUTH_LEFT, LM.MOUTH_RIGHT], zOffset: -0.02 },
  { index: MOUTH_MID, sources: [LM.MOUTH_LEFT, LM.MOUTH_RIGHT], zOffset: -0.015 },

  // Arms — linear midpoints, no Z offset (arms are roughly tubular)
  { index: LEFT_UPPER_ARM_MID, sources: [LM.LEFT_SHOULDER, LM.LEFT_ELBOW] },
  { index: LEFT_FOREARM_MID, sources: [LM.LEFT_ELBOW, LM.LEFT_WRIST] },
  { index: RIGHT_UPPER_ARM_MID, sources: [LM.RIGHT_SHOULDER, LM.RIGHT_ELBOW] },
  { index: RIGHT_FOREARM_MID, sources: [LM.RIGHT_ELBOW, LM.RIGHT_WRIST] },

  // Legs
  { index: LEFT_THIGH_MID, sources: [LM.LEFT_HIP, LM.LEFT_KNEE] },
  { index: LEFT_SHIN_MID, sources: [LM.LEFT_KNEE, LM.LEFT_ANKLE] },
  { index: RIGHT_THIGH_MID, sources: [LM.RIGHT_HIP, LM.RIGHT_KNEE] },
  { index: RIGHT_SHIN_MID, sources: [LM.RIGHT_KNEE, LM.RIGHT_ANKLE] },
];

/** Total vertex count = real landmarks + synthetic. */
export const TOTAL_VERTEX_COUNT = REAL_COUNT + SYNTHETIC_VERTICES.length;

// ============================================================================
// Base triangle list (before subdivision)
// ============================================================================

export type Triangle = [number, number, number];

export const BODY_MESH_TRIANGLES: Triangle[] = [
  // ---------- Head / face ----------
  // Forehead triangles (brows to forehead midpoint)
  [LM.LEFT_EYE, FOREHEAD, LM.RIGHT_EYE],
  [LM.LEFT_EAR, FOREHEAD, LM.LEFT_EYE],
  [LM.RIGHT_EYE, FOREHEAD, LM.RIGHT_EAR],

  // Eye to nose
  [LM.LEFT_EYE, LM.NOSE, LM.RIGHT_EYE],
  [LM.LEFT_EYE_OUTER, LM.LEFT_EYE, LM.NOSE],
  [LM.NOSE, LM.RIGHT_EYE, LM.RIGHT_EYE_OUTER],

  // Cheeks (ears to mouth corners)
  [LM.LEFT_EAR, LM.LEFT_EYE, LM.MOUTH_LEFT],
  [LM.LEFT_EYE, LM.NOSE, LM.MOUTH_LEFT],
  [LM.NOSE, LM.MOUTH_RIGHT, LM.RIGHT_EYE],
  [LM.RIGHT_EYE, LM.MOUTH_RIGHT, LM.RIGHT_EAR],

  // Nose → mouth
  [LM.NOSE, LM.MOUTH_LEFT, LM.MOUTH_RIGHT],

  // Chin / jaw
  [LM.MOUTH_LEFT, CHIN, LM.MOUTH_RIGHT],
  [LM.LEFT_EAR, LM.MOUTH_LEFT, CHIN],
  [CHIN, LM.MOUTH_RIGHT, LM.RIGHT_EAR],

  // Neck (ears down to shoulders with chin in the middle)
  [LM.LEFT_EAR, CHIN, LM.LEFT_SHOULDER],
  [CHIN, LM.RIGHT_SHOULDER, LM.RIGHT_EAR],
  [CHIN, LM.LEFT_SHOULDER, LM.RIGHT_SHOULDER],

  // ---------- Torso (curved, split with midline) ----------
  // Upper torso: shoulders to chest, chest to sternum
  [LM.LEFT_SHOULDER, CHEST, LM.RIGHT_SHOULDER],
  [LM.LEFT_SHOULDER, STERNUM, CHEST],
  [CHEST, STERNUM, LM.RIGHT_SHOULDER],

  // Mid torso: shoulders-sternum-navel-hips
  [LM.LEFT_SHOULDER, LM.LEFT_HIP, STERNUM],
  [STERNUM, LM.LEFT_HIP, NAVEL],
  [STERNUM, NAVEL, LM.RIGHT_HIP],
  [STERNUM, LM.RIGHT_HIP, LM.RIGHT_SHOULDER],

  // Lower torso: hips to belly
  [LM.LEFT_HIP, BELLY, LM.RIGHT_HIP],
  [LM.LEFT_HIP, NAVEL, BELLY],
  [NAVEL, LM.RIGHT_HIP, BELLY],

  // ---------- Left arm (tubular with midpoint) ----------
  [LM.LEFT_SHOULDER, LM.LEFT_ELBOW, LEFT_UPPER_ARM_MID],
  [LM.LEFT_SHOULDER, LEFT_UPPER_ARM_MID, CHEST],
  [LEFT_UPPER_ARM_MID, LM.LEFT_ELBOW, LM.LEFT_HIP],
  [LEFT_UPPER_ARM_MID, LM.LEFT_HIP, CHEST],
  [LM.LEFT_ELBOW, LM.LEFT_WRIST, LEFT_FOREARM_MID],
  [LM.LEFT_ELBOW, LEFT_FOREARM_MID, LM.LEFT_HIP],

  // ---------- Right arm ----------
  [LM.RIGHT_SHOULDER, RIGHT_UPPER_ARM_MID, LM.RIGHT_ELBOW],
  [LM.RIGHT_SHOULDER, CHEST, RIGHT_UPPER_ARM_MID],
  [RIGHT_UPPER_ARM_MID, LM.RIGHT_HIP, LM.RIGHT_ELBOW],
  [RIGHT_UPPER_ARM_MID, CHEST, LM.RIGHT_HIP],
  [LM.RIGHT_ELBOW, RIGHT_FOREARM_MID, LM.RIGHT_WRIST],
  [LM.RIGHT_ELBOW, LM.RIGHT_HIP, RIGHT_FOREARM_MID],

  // ---------- Hands ----------
  [LM.LEFT_WRIST, LM.LEFT_THUMB, LM.LEFT_PINKY],
  [LM.LEFT_WRIST, LM.LEFT_PINKY, LM.LEFT_INDEX],
  [LM.RIGHT_WRIST, LM.RIGHT_PINKY, LM.RIGHT_THUMB],
  [LM.RIGHT_WRIST, LM.RIGHT_INDEX, LM.RIGHT_PINKY],

  // ---------- Left leg ----------
  [LM.LEFT_HIP, LEFT_THIGH_MID, LM.LEFT_KNEE],
  [LM.LEFT_HIP, LM.LEFT_KNEE, BELLY],
  [LM.LEFT_KNEE, LEFT_SHIN_MID, LM.LEFT_ANKLE],
  [LM.LEFT_KNEE, LM.LEFT_ANKLE, BELLY],

  // ---------- Right leg ----------
  [LM.RIGHT_HIP, LM.RIGHT_KNEE, RIGHT_THIGH_MID],
  [LM.RIGHT_HIP, BELLY, LM.RIGHT_KNEE],
  [LM.RIGHT_KNEE, LM.RIGHT_ANKLE, RIGHT_SHIN_MID],
  [LM.RIGHT_KNEE, BELLY, LM.RIGHT_ANKLE],

  // ---------- Feet ----------
  [LM.LEFT_ANKLE, LM.LEFT_HEEL, LM.LEFT_FOOT_INDEX],
  [LM.RIGHT_ANKLE, LM.RIGHT_FOOT_INDEX, LM.RIGHT_HEEL],
];

// ============================================================================
// Subdivision
// ============================================================================

/**
 * Recursively subdivide a triangle. Each subdivision turns one triangle into
 * 4 by splitting each edge at its midpoint and connecting them.
 *
 * Example with depth=1 on triangle (A,B,C):
 *
 *        A
 *       / \
 *      AB--CA
 *     / \ / \
 *    B---BC--C
 *
 * → (A,AB,CA), (AB,B,BC), (CA,BC,C), (AB,BC,CA)
 */
export function subdivideTriangle(
  tri: Triangle,
  depth: number
): Array<[number, number, number]> {
  if (depth <= 0) return [tri];

  // Subdivision produces indices into the vertex array that don't exist as
  // integer landmark indices. Instead of allocating a global vertex buffer
  // we encode each midpoint as a virtual index > TOTAL_VERTEX_COUNT and
  // resolve them at render time via a per-frame cache. To keep this
  // stateless, we use tuple keys: the renderer computes midpoint positions
  // on the fly.
  //
  // For simplicity and GC-friendliness, we return *resolved* sub-triangles
  // in a different shape — see subdivideTriangleResolved below.
  throw new Error('Use subdivideTriangleResolved instead');
}

/**
 * A resolved mesh vertex — either a real landmark (by index) or a synthetic
 * point computed from a list of source indices with weights.
 *
 * We express sub-triangles as [ResolvedVertex, ResolvedVertex, ResolvedVertex]
 * so the renderer can compute each vertex's 2D and 3D position once per frame.
 */
export interface ResolvedVertex {
  /** Source vertex indices (0..TOTAL_VERTEX_COUNT-1). */
  sources: number[];
  /** Normalized weights summing to 1. */
  weights: number[];
}

export type ResolvedTriangle = [ResolvedVertex, ResolvedVertex, ResolvedVertex];

function v(i: number): ResolvedVertex {
  return { sources: [i], weights: [1] };
}

function midpoint(a: ResolvedVertex, b: ResolvedVertex): ResolvedVertex {
  const sources: number[] = [];
  const weights: number[] = [];
  const push = (idx: number, w: number) => {
    const existing = sources.indexOf(idx);
    if (existing >= 0) {
      weights[existing] += w;
    } else {
      sources.push(idx);
      weights.push(w);
    }
  };
  for (let i = 0; i < a.sources.length; i++) push(a.sources[i], a.weights[i] * 0.5);
  for (let i = 0; i < b.sources.length; i++) push(b.sources[i], b.weights[i] * 0.5);
  return { sources, weights };
}

function subdivideRecursive(
  a: ResolvedVertex,
  b: ResolvedVertex,
  c: ResolvedVertex,
  depth: number,
  out: ResolvedTriangle[]
): void {
  if (depth <= 0) {
    out.push([a, b, c]);
    return;
  }
  const ab = midpoint(a, b);
  const bc = midpoint(b, c);
  const ca = midpoint(c, a);
  subdivideRecursive(a, ab, ca, depth - 1, out);
  subdivideRecursive(ab, b, bc, depth - 1, out);
  subdivideRecursive(ca, bc, c, depth - 1, out);
  subdivideRecursive(ab, bc, ca, depth - 1, out);
}

/**
 * Pre-compute the subdivided mesh. Called once at module load so render-time
 * work is just position lookups + shading.
 */
export function buildSubdividedMesh(depth: number): ResolvedTriangle[] {
  const out: ResolvedTriangle[] = [];
  for (const tri of BODY_MESH_TRIANGLES) {
    subdivideRecursive(v(tri[0]), v(tri[1]), v(tri[2]), depth, out);
  }
  return out;
}

// 2-level subdivision = 16 sub-triangles per base triangle.
// Base triangles: ~55. Total: ~880 triangles.
export const SUBDIVIDED_MESH = buildSubdividedMesh(2);

// ============================================================================
// Math helpers
// ============================================================================

/**
 * Compute face normal of a triangle (unit vector). Uses cross product of
 * two edge vectors.
 */
export function computeFaceNormal(
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
  c: { x: number; y: number; z: number }
): { nx: number; ny: number; nz: number } {
  const ux = b.x - a.x;
  const uy = b.y - a.y;
  const uz = b.z - a.z;
  const vx = c.x - a.x;
  const vy = c.y - a.y;
  const vz = c.z - a.z;
  let nx = uy * vz - uz * vy;
  let ny = uz * vx - ux * vz;
  let nz = ux * vy - uy * vx;
  const len = Math.hypot(nx, ny, nz);
  if (len === 0) return { nx: 0, ny: 0, nz: 1 };
  return { nx: nx / len, ny: ny / len, nz: nz / len };
}
